import { useEffect, useMemo, useState } from 'react'
import type { Decisao } from '@services/db.js'
import type { NovaDecisao } from '@services/gestao.js'
import { DECISOES_CATALOGO, type DecisaoCatalogo } from '@content/decisoes-catalogo.js'
import { FASES_CONSTRUTIVAS } from '@utils/constants.js'
import { Modal } from '@components/primitives/Modal.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { Select } from '@components/primitives/Select.js'
import { Textarea } from '@components/primitives/Textarea.js'

interface Props {
  open: boolean
  initial?: Decisao | null
  catalogo?: DecisaoCatalogo | null
  onClose: () => void
  onSalvar: (input: NovaDecisao) => Promise<void>
  onEditar?: (id: number, input: Partial<NovaDecisao>) => Promise<void>
}

const EMPTY = {
  titulo: '',
  escolha: '',
  faseId: 'pre-obra',
  travaEm: '',
  motivo: '',
}

export function DecisaoFormModal({ open, initial, catalogo, onClose, onSalvar, onEditar }: Props) {
  const [form, setForm] = useState(EMPTY)
  const [chave, setChave] = useState<string | undefined>(undefined)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!open) return
    if (initial) {
      setForm({
        titulo: initial.titulo,
        escolha: initial.escolha,
        faseId: initial.faseId,
        travaEm: initial.travaEm ?? '',
        motivo: initial.motivo ?? '',
      })
      setChave(initial.chave)
      return
    }
    if (catalogo) {
      setForm({
        titulo: catalogo.titulo,
        escolha: '',
        faseId: catalogo.faseId,
        travaEm: catalogo.travaEm,
        motivo: catalogo.motivo,
      })
      setChave(catalogo.chave)
      return
    }
    setForm(EMPTY)
    setChave(undefined)
  }, [open, initial, catalogo])

  const itemCatalogo = useMemo(
    () => (chave ? DECISOES_CATALOGO.find((c) => c.chave === chave) : undefined),
    [chave],
  )

  const canSubmit = form.titulo.trim().length > 1 && form.escolha.trim().length > 1

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSubmit) return
    setSalvando(true)
    const input: NovaDecisao = {
      titulo: form.titulo.trim(),
      escolha: form.escolha.trim(),
      faseId: form.faseId,
      travada: true,
      ...(chave ? { chave } : {}),
      ...(form.travaEm ? { travaEm: form.travaEm } : {}),
      ...(form.motivo.trim() ? { motivo: form.motivo.trim() } : {}),
    }
    if (initial && onEditar && initial.id != null) await onEditar(initial.id, input)
    else await onSalvar(input)
    setSalvando(false)
    onClose()
  }

  const faseOpts = FASES_CONSTRUTIVAS.map((f) => ({ value: f.id, label: f.label }))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? 'Editar decisão' : 'Registrar decisão'}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            form='decisao-form'
            loading={salvando}
            disabled={!canSubmit}
          >
            Salvar
          </Button>
        </div>
      }
    >
      <form id='decisao-form' className='cf3-gestao-form' onSubmit={(e) => void handleSubmit(e)}>
        <Input
          label='O que ficou combinado'
          value={form.titulo}
          onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
          required
        />
        <Textarea
          label='A escolha'
          value={form.escolha}
          onChange={(e) => setForm((f) => ({ ...f, escolha: e.target.value }))}
          placeholder={itemCatalogo?.placeholder ?? 'O que foi decidido, com medida ou marca.'}
          required
        />
        <Select
          label='Fase da escolha'
          value={form.faseId}
          options={faseOpts}
          onChange={(e) => setForm((f) => ({ ...f, faseId: e.target.value }))}
        />
        {form.motivo && <p className='cf3-gestao-aviso'>{form.motivo}</p>}
      </form>
    </Modal>
  )
}
